import superjson from 'superjson';
import { uploadChatAttachment, type OutputType } from './upload_POST.schema';

const DIRECT_UPLOAD_THRESHOLD = 4 * 1024 * 1024; // 4MB

/**
 * Smart upload — automatically uses direct binary upload for large files
 * and base64 upload for small files. Pass the raw File object.
 */
export const uploadChatAttachmentSmart = async (
  file: File,
  opts: { sessionId: string; fileName: string; fileType?: string },
): Promise<OutputType> => {
  if (file.size > DIRECT_UPLOAD_THRESHOLD) {
    return uploadChatAttachmentDirect(file, opts);
  }
  // Small file — read as data URL and use existing endpoint
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
  return uploadChatAttachment({
    sessionId: opts.sessionId,
    fileName: opts.fileName,
    fileType: opts.fileType,
    fileSize: file.size,
    dataUrl,
  });
};

/**
 * Direct binary upload — sends the raw file via FormData instead of base64.
 * Use this for large files (>4MB) to avoid Worker CPU/memory limits.
 */
export const uploadChatAttachmentDirect = async (
  file: File | Blob,
  opts: {
    sessionId: string;
    fileName: string;
    fileType?: string;
  },
  init?: RequestInit,
): Promise<OutputType> => {
  const formData = new FormData();
  formData.append('file', file, opts.fileName);
  formData.append('sessionId', opts.sessionId);
  formData.append('fileName', opts.fileName);
  if (opts.fileType) {
    formData.append('fileType', opts.fileType);
  }

  const result = await fetch('/_api/chat-attachments/upload-direct', {
    method: 'POST',
    credentials: 'include',
    body: formData,
    ...init,
    // Don't set Content-Type — browser sets it with boundary for FormData
  });

  if (!result.ok) {
    const raw = await result.text();
    if (raw.trimStart().startsWith('<!DOCTYPE') || raw.trimStart().startsWith('<html')) {
      throw new Error(
        result.status === 503
          ? 'Server temporarily unavailable — try again.'
          : `Upload failed (${result.status})`,
      );
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(raw);
      throw new Error(errorObject.error);
    } catch (e) {
      if (e instanceof Error && !e.message.includes('Unexpected token')) throw e;
      throw new Error(`Upload failed (${result.status})`);
    }
  }

  const raw = await result.text();
  try {
    return superjson.parse<OutputType>(raw);
  } catch {
    throw new Error('Invalid response from server');
  }
};
